import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  button: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    maxWidth: 5150,
    minWidth: 200,
    boxShadow: theme.shadows[3],
    transition: '300ms',
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border.light}`,
    borderRadius: 12,
    padding: 16,
    '&:hover': {
      cursor: 'pointer',
      boxShadow: theme.shadows[5],
      background: theme.palette.action.hover,
      backgroundColor: theme.palette.action.hover,
    },
  },
  secondaryText: {
    color: theme.palette.text.secondary,
  },
  disabled: {
    opacity: 0.5,
  },
  mainText: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  textSummary: {
    flex: 1,
    width: '100%',
    marginTop: 8,
    padding: 16,
    verticalAlign: 'middle',
    borderRadius: 12,
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.dark,
  },
}));

const QFreqButton = React.forwardRef((props, ref) => {
  const { dueOn, endOn, frequency, summaryText, className, onClick } = props;
  const { classes, cx } = useStyles();

  const handleKeyDown = (e) => {
    if (onClick && e.keyCode === 13) onClick();
  };

  return (
    <ButtonBase
      id='frequencyButton'
      data-testid='frequencyButton'
      component={Paper}
      elevation={4}
      onKeyDown={handleKeyDown}
      onClick={onClick}
      classes={{ disabled: classes.disabled }}
      ref={ref}
      className={cx(classes.button, className)}
      sharedcomponentid='Q_FREQ_BUTTON'
    >
      <Box display='flex' alignItems='center' justifyContent='space-between' width='100%' height='100%'>
        <Box display='flex' gap='40px'>
          <Box display='flex' flexDirection='column' justifyContent='space-between'>
            <Typography variant='captionRegular' className={classes.secondaryText}>
              Start Date
            </Typography>
            <Typography className={classes.mainText} variant='bodyRegular'>
              {DateTime.fromISO(dueOn).isValid ? DateTime.fromISO(dueOn).toLocaleString(DateTime.DATE_MED) : '-'}
            </Typography>
          </Box>
          <Box display='flex' flexDirection='column' justifyContent='space-between'>
            <Typography variant='captionRegular' className={classes.secondaryText}>
              Frequency
            </Typography>
            <Typography className={classes.mainText} variant='bodyRegular'>
              {frequency?.replace(/[0-9]/g, 'X')}
            </Typography>
          </Box>
          <Box display='flex' flexDirection='column' justifyContent='space-between'>
            <Typography variant='captionRegular' className={classes.secondaryText}>
              End Date
            </Typography>
            <Typography className={classes.mainText} variant='bodyRegular'>
              {endOn ? DateTime.fromISO(endOn).toLocaleString(DateTime.DATE_MED) : '-'}
            </Typography>
          </Box>
        </Box>

        <Button variant='text' id='change-freq' endIcon={<ChevronRightRoundedIcon />}>
          Change
        </Button>
      </Box>

      {summaryText ? (
        <Typography variant='captionRegular' className={classes.textSummary}>
          {summaryText}
        </Typography>
      ) : null}
    </ButtonBase>
  );
});

QFreqButton.displayName = 'QFreqButton';

QFreqButton.propTypes = {
  className: PropTypes.string,
  onClick: PropTypes.func,
  dueOn: PropTypes.object,
  endOn: PropTypes.object,
  frequency: PropTypes.string,
  summaryText: PropTypes.string,
};

export default QFreqButton;
